/**
 * Format-agnostic barcode matching.
 *
 * UPC-A, EAN-8, EAN-13 and GTIN-14 are all the same product number expressed
 * with a different amount of leading zeros plus a trailing check digit. A
 * barcode scanned as a 12-digit UPC-A, its 13-digit EAN-13 form (leading `0`)
 * and its 14-digit GTIN-14 form therefore all describe the same item — and may
 * need to match a value stored without the number-system and/or check digit
 * (e.g. a 10-digit UPC core).
 *
 * Rather than slicing a fixed number of digits, we reduce both the scanned and
 * the stored value to a small set of canonical keys and match when those sets
 * intersect.
 */

/** GTIN lengths that carry a trailing check digit. */
const GTIN_LENGTHS: ReadonlySet<number> = new Set([8, 12, 13, 14]);

/**
 * Reduces a barcode to its canonical comparison keys:
 * - the digits with leading zeros removed, and
 * - when the input is a full GTIN length, the same value with its trailing
 *   check digit removed (so a 10-digit stored core matches a 12-digit UPC-A).
 *
 * @param code raw barcode value (may contain spaces/hyphens).
 * @returns the set of canonical keys (empty when there are no digits).
 */
function canonicalKeys(code: string): Set<string> {
  const digits: string = (code ?? '').replace(/\D/g, '');
  const keys = new Set<string>();
  if (!digits) {
    return keys;
  }
  keys.add(digits.replace(/^0+/, '') || '0');
  if (GTIN_LENGTHS.has(digits.length)) {
    keys.add(digits.slice(0, -1).replace(/^0+/, '') || '0');
  }
  return keys;
}

/**
 * Returns true when a scanned barcode and a stored barcode refer to the same
 * product, regardless of UPC/EAN/GTIN representation or leading-zero padding.
 *
 * @param scanned the value captured by the scanner or typed manually.
 * @param stored the product's stored barcode number.
 */
export function barcodesMatch(scanned: string, stored: string): boolean {
  const scannedKeys: Set<string> = canonicalKeys(scanned);
  if (!scannedKeys.size) {
    return false;
  }
  for (const key of canonicalKeys(stored)) {
    if (scannedKeys.has(key)) {
      return true;
    }
  }
  return false;
}
